import { FC, ReactNode, useState } from 'react';
import { Box, Typography, Stack, Tooltip, Button, Chip, IconButton } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import DescriptionIcon from '@mui/icons-material/Description';
import { Horse } from '../utils/api';
import { formatBreed } from '../utils/breed';
import { INSTANT_FORMATS, calendarDaysUntil, computeAgeYears, formatCalendarDate, formatDob } from '../utils/dateUtils';
import { COGGINS_NO_EXPIRY_LABEL } from '../utils/coggins';
import { downloadAuthedBlob } from '../hooks/useAuthedBlobUrl';
import { useClinicTimezone, useFormatInstant } from '../hooks/useClinicTimezone';
import { formatDollars } from '../utils/money';
import HorsePhotoAvatar from './HorsePhotoAvatar';

/**
 * Patient header card — the at-a-glance identity/clinical/ownership summary at
 * the top of a patient chart. Has two states: active and deceased. The deceased
 * state is a read-only, gray-ramped view driven purely by `horse.status`
 * (the backend auto-flips it when a date of death is recorded).
 *
 * Coggins/vaccination badge THRESHOLD logic lives here (per spec): the parent
 * passes the raw expiry date + next-due record and this component computes the
 * days-remaining state, so the data layer stays dumb.
 */

export interface PatientHeaderStats {
  lastVisitDate?: string | null;
  totalRecords?: number;
  /** Outstanding balance in dollars; red emphasis when > 0. */
  balance?: number;
  openInvoiceCount?: number;
  /**
   * A5c: money in flight (an ACH clearing), in dollars, ALREADY subtracted from
   * `balance`. Rendered as a hint under the Balance chip so a figure that fell
   * says why it fell.
   */
  processingAmount?: number;
}

/** The header's vaccination counts: `chartDates.vaccinationSummary`, the same standings as the Vaccinations tab. */
export interface VaccinationSummary {
  overdue: number;
  dueSoon: number;
  current: number;
  noBooster: number;
  total: number;
}

interface PatientHeaderCardProps {
  horse: Horse;
  stats?: PatientHeaderStats;
  /**
   * Raw Coggins/EIA expiry — a CALENDAR DAY, from whichever source recorded the
   * horse's newest Coggins: `LabResult.expiryDate` (`@db.Date`, so it arrives at
   * UTC midnight) or the legacy `Vaccination.nextDueDate`. The parent picks the
   * winner; this card only renders it.
   */
  cogginsExpiry?: string | null;
  /**
   * Whether the horse has a Coggins record AT ALL, of either kind.
   *
   * Separate from `cogginsExpiry` because the two facts are independent and the
   * badge has to tell them apart: a record with no expiry is neither "No record"
   * (false, and the complaint this feature exists to fix) nor safe to show as
   * current. Every pre-existing row is in that state — the expiry column shipped
   * additive with no backfill on purpose.
   */
  cogginsHasRecord?: boolean;
  /** Coggins accession number — the NUMBER off the certificate. Only a
   *  `LabResult` carries one; a legacy vaccination-path Coggins has none. */
  cogginsAccession?: string | null;
  /** The stored result says carrier. Renders red "Positive" whatever the expiry says. */
  cogginsPositive?: boolean;
  /** `LabResult.pdfAttachmentId` — when set, the badge offers the certificate
   *  itself, so staff do not have to go via the Labs tab to see the paperwork. */
  cogginsCertificateAttachmentId?: string | null;
  /** How many of the horse's vaccines are in each state; omitted hides the counts. */
  vaccinationSummary?: VaccinationSummary;
  /** Clicking the counts opens the Vaccinations tab. */
  onVaccinationsClick?: () => void;
  /** Right-aligned action controls (chart toggle, contact, info buttons…). */
  actions?: ReactNode;
  /** Called when the user clicks "+ Add Alert". Omit to hide the button. */
  onAddAlert?: () => void;
  /** Active medical alerts to display alongside the Add Alert button. */
  alerts?: Array<{ id: string; text: string; isCritical: boolean }>;
  /** Whether the current user has billing permissions. Hides balance/invoice stats when false. */
  showBilling?: boolean;
}

// Per-owner avatar colors — first owner blue, second teal, then a fixed ramp.
const OWNER_COLORS = ['#1F6FEB', '#0E7490', '#7C3AED', '#B45309', '#BE185D'];

// Spec badge palettes (literal hex — design-system-agnostic).
const BADGE = {
  green: { bg: '#EAF3DE', fg: '#3B6D11' },
  amber: { bg: '#FAEEDA', fg: '#854F0B' },
  red: { bg: '#FCEBEB', fg: '#A32D2D' },
};

// Deceased gray ramp.
const DEC = {
  cardBg: '#F1EFE8',
  cardBorder: '#B4B2A9',
  label: '#888780',
  value: '#5F5E5A',
  heading: '#444441',
  chipBg: '#E8E6DF',
  pillBg: '#D3D1C7',
  pillFg: '#5F5E5A',
};

const SUBTLE_BG = '#F5F3EF';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * ONE display format for every date on this card, whatever its KIND.
 *
 * The card carries both kinds side by side and they are formatted by DIFFERENT
 * helpers on purpose (Docs/architecture/DATE-KINDS.md):
 *
 *   Date of birth   `Horse.dateOfBirth`        CALENDAR (§1.1) -> `formatDob`
 *   Date of death   `Horse.dateOfDeath`        CALENDAR (§1.1) -> `formatCalendarDate`
 *   Last visit      `Appointment.date`         INSTANT   (§1.3) -> `useFormatInstant`
 *
 * A shared FORMAT is not a shared RULE. Until 2026-09-04 THREE of the four —
 * last visit, date of death and the vaccination due date — went through one
 * local `fmtDate` helper spelled `new Date(x).toLocaleDateString()`. Date of
 * birth was ALREADY on `formatDob`, and that mismatched PAIR is exactly what the
 * clinic reported: `fmtDate` printed `8/13/2026` for last visit beside
 * `formatDob`'s `Nov 3, 2020` in the chip next to it. The same helper also
 * re-zoned the OTHER two — both CALENDAR days — into the viewer's browser, the
 * day-rollback every US clinic sees; nobody had reported that one, which is why
 * the fix could not stop at the two chips the ticket named. It was invisible to
 * `scripts/check-date-idioms.js` because it was spelled across two statements,
 * the documented multi-line gap in that script's header.
 *
 * `INSTANT_FORMATS.date` is 'MMM d, yyyy' — the same string `formatDob`
 * defaults to, which is what makes the four cells agree on the page.
 */
const DAY_FORMAT = INSTANT_FORMATS.date;

/** Small colored pill badge with an optional leading icon. */
const Badge: FC<{ bg: string; fg: string; icon?: ReactNode; children: ReactNode }> = ({ bg, fg, icon, children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      bgcolor: bg,
      color: fg,
      borderRadius: '999px',
      px: 0.9,
      py: 0.2,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.6,
      whiteSpace: 'nowrap',
    }}
  >
    {icon}
    {children}
  </Box>
);

/**
 * The Coggins / EIA badge, in FIVE states.
 *
 * It had three, and the missing pair is the whole point of this change: until
 * `LabResult.expiryDate` existed, a horse whose Coggins was recorded as a lab
 * result showed "No record" — the chart flatly denying paperwork the clinic was
 * holding in its hand.
 *
 *   no record at all              grey   "No record"
 *   record, no usable expiry      grey   "On file · expiry unknown"   ← NEW
 *   expiry > 60 days out          green  "Current"
 *   expiry 30–60 days out         amber  "Nd left"
 *   expiry < 30 days / past       red    "Nd left" / "Expired MM/dd/yy"
 *
 * ⚠ TWO DATE RULES HERE AND THEY ARE BOTH DELIBERATE.
 *
 * The DAY is printed with `formatCalendarDate`, which parses the date PART and
 * never consults a zone. Not `fmtDate`/`DAY_FORMAT`'s neighbours by accident:
 * a `@db.Date` value arrives as UTC midnight, and `new Date(x).toLocaleDateString()`
 * on it prints the PREVIOUS day in every US zone — the rollback class
 * `scripts/check-date-idioms.js` exists to hold at zero.
 *
 * The COUNTDOWN is `calendarDaysUntil`, a difference of two DAYS. Subtracting
 * `Date.now()` from the stored instant would call a UTC-midnight expiry
 * "expired" from 8 PM Eastern on the day BEFORE it lapses, and the number it
 * shows is the one a clinician reads off the screen to decide whether a horse
 * can ship. "Today" is the CLINIC's day (`useClinicTimezone`), so a remote
 * viewer reads the same count as the front desk.
 */
const CogginsBadge: FC<{ expiry?: string | null; hasRecord?: boolean; positive?: boolean }> = ({ expiry, hasRecord, positive }) => {
  const timeZone = useClinicTimezone();
  if (positive) {
    // A carrier. The date is when the test was valid to, not when the horse
    // becomes safe - context beside the word, never a countdown, never green.
    const posDay = formatCalendarDate(expiry, 'MM/dd/yy');
    return <Badge bg={BADGE.red.bg} fg={BADGE.red.fg}>{posDay ? `Positive · ${posDay}` : 'Positive'}</Badge>;
  }
  const days = calendarDaysUntil(expiry, timeZone);
  const clock = <AccessTimeIcon sx={{ fontSize: 12 }} />;
  if (days === null) {
    // An expiry we cannot state is NOT the same as no paperwork. Distinguishing
    // them is what stops a real Coggins reading as missing — and the "unknown"
    // wording is the nudge that gets someone to open Edit and fill it in.
    if (hasRecord) {
      return <Badge bg={SUBTLE_BG} fg="#6B6A66">{COGGINS_NO_EXPIRY_LABEL}</Badge>;
    }
    return <Badge bg={SUBTLE_BG} fg="#6B6A66">No record</Badge>;
  }
  const day = formatCalendarDate(expiry, 'MM/dd/yy');
  if (days < 0) {
    return (
      <Badge bg={BADGE.red.bg} fg={BADGE.red.fg} icon={clock}>
        {day ? `Expired ${day}` : 'Expired'}
      </Badge>
    );
  }
  if (days < 30) return <Badge bg={BADGE.red.bg} fg={BADGE.red.fg} icon={clock}>{days}d left</Badge>;
  if (days <= 60) return <Badge bg={BADGE.amber.bg} fg={BADGE.amber.fg} icon={clock}>{days}d left</Badge>;
  return (
    <Badge bg={BADGE.green.bg} fg={BADGE.green.fg} icon={clock}>
      {day ? `Current · ${day}` : 'Current'}
    </Badge>
  );
};

/**
 * The certificate button.
 *
 * A component rather than an inline handler for one reason: the fetch can FAIL,
 * and an un-awaited `downloadAuthedBlob(...)` in an `onClick` turns that failure
 * into an unhandled promise rejection. This card has no `SnackbarProvider` in its
 * render contract (its own suites mount it bare), so the failure is reported
 * where it happened — the tooltip — instead of being thrown into the void or
 * swallowed silently.
 */
const CogginsCertificateButton: FC<{ attachmentId: string; horseName: string }> = ({
  attachmentId,
  horseName,
}) => {
  const [failed, setFailed] = useState(false);
  const open = async () => {
    setFailed(false);
    try {
      await downloadAuthedBlob(`/attachments/${attachmentId}/download`, `coggins-${horseName}.pdf`);
    } catch {
      setFailed(true);
    }
  };
  return (
    <Tooltip title={failed ? "Couldn't open the certificate — try again" : 'Open the Coggins certificate'}>
      <IconButton size="small" onClick={open} data-testid="coggins-certificate-link" sx={{ p: 0.25 }}>
        <DescriptionIcon sx={{ fontSize: 15, ...(failed && { color: 'error.main' }) }} />
      </IconButton>
    </Tooltip>
  );
};

/** Counts by state, worst first, each saying what it is; a horse with nothing recorded says so. */
const VaccinationCounts: FC<{ summary: VaccinationSummary; onClick?: () => void }> = ({ summary, onClick }) => {
  if (summary.total === 0) return <Badge bg={SUBTLE_BG} fg="#6B6A66">None recorded</Badge>;
  const parts = [
    { n: summary.overdue, label: 'overdue', ...BADGE.red },
    { n: summary.dueSoon, label: 'due soon', ...BADGE.amber },
    { n: summary.current, label: 'current', ...BADGE.green },
    { n: summary.noBooster, label: 'no booster', bg: SUBTLE_BG, fg: '#6B6A66' },
  ].filter((p) => p.n > 0);
  return (
    <Box
      component={onClick ? 'button' : 'span'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      aria-label={onClick ? 'Open the Vaccinations tab' : undefined}
      sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap', p: 0, border: 0, bgcolor: 'transparent', cursor: onClick ? 'pointer' : 'default', font: 'inherit' }}
    >
      {parts.map((p) => (
        <Badge key={p.label} bg={p.bg} fg={p.fg}>
          {p.n} {p.label}
        </Badge>
      ))}
    </Box>
  );
};

const StatChip: FC<{ label: string; value: ReactNode; deceased: boolean; alert?: boolean; testId?: string; hint?: string }> = ({ label, value, deceased, alert, testId, hint }) => (
  <Box
    sx={{
      bgcolor: deceased ? DEC.chipBg : SUBTLE_BG,
      borderRadius: 1.5,
      px: 1.5,
      py: 1,
      border: alert ? '1px solid #A32D2D' : '1px solid transparent',
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: deceased ? DEC.label : 'text.secondary' }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: 13, fontWeight: 500, color: alert ? '#A32D2D' : deceased ? DEC.value : 'text.primary' }}
      data-testid={testId}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 9.5, color: deceased ? DEC.label : 'text.secondary', mt: 0.25 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const MetaCell: FC<{ label: string; value: ReactNode; deceased: boolean }> = ({ label, value, deceased }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: deceased ? DEC.label : 'text.secondary', mb: 0.25 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 13, fontWeight: 500, color: deceased ? DEC.value : 'text.primary', display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
      {value}
    </Box>
  </Box>
);

const PatientHeaderCard: FC<PatientHeaderCardProps> = ({
  horse,
  stats = {},
  cogginsExpiry,
  cogginsHasRecord,
  cogginsAccession,
  cogginsPositive,
  cogginsCertificateAttachmentId,
  vaccinationSummary,
  onVaccinationsClick,
  actions,
  onAddAlert,
  alerts = [],
  showBilling = false,
}) => {
  // INSTANT plane — bound to the clinic's zone (the viewer's until a clinic sets
  // one, ADR-0034). Only `stats.lastVisitDate` uses it; every other date on this
  // card is a calendar day and must never touch a zone.
  const formatInstant = useFormatInstant();
  const deceased = horse.status === 'DECEASED';
  const age = computeAgeYears(horse.dateOfBirth);
  const owners = horse.owners || [];
  const totalPct = owners.reduce((sum, o) => sum + (o.ownershipPercentage ?? 0), 0);
  const balance = stats.balance ?? 0;
  const hasBalance = balance > 0;

  const breed = formatBreed(horse) || null;
  const subtitleParts = [
    breed,
    horse.gender,
    // The DOB itself lives in the stat-chip row below; the subtitle keeps
    // only the derived age.
    age !== null ? `${age} years old` : null,
    horse.color,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        bgcolor: deceased ? DEC.cardBg : 'background.paper',
        border: '0.5px solid',
        borderColor: deceased ? DEC.cardBorder : 'divider',
        borderRadius: 2.5,
        p: { xs: 2, sm: '1.25rem 1.5rem' },
      }}
      data-testid="patient-header-card"
    >
      {/* Upper zone: identity+stats | divider | ownership */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left — identity */}
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
          <HorsePhotoAvatar
            horseId={horse.id}
            size={44}
            sx={{
              bgcolor: deceased ? DEC.chipBg : SUBTLE_BG,
              border: '0.5px solid',
              borderColor: deceased ? DEC.cardBorder : 'divider',
              flexShrink: 0,
              opacity: deceased ? 0.7 : 1,
            }}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* Name row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 20, fontWeight: 500, color: deceased ? DEC.heading : 'text.primary', lineHeight: 1.2 }}>
                {horse.name}
              </Typography>
              {horse.patientId && (
                <Typography component="span" sx={{ fontSize: 13, color: deceased ? DEC.label : 'text.secondary' }}>
                  {horse.patientId}
                </Typography>
              )}
              {deceased ? (
                <>
                  <Badge bg="#7F1D1D" fg="#fff" icon={<CancelIcon sx={{ fontSize: 12 }} />}>Deceased</Badge>
                  <Badge bg={DEC.pillBg} fg={DEC.pillFg} icon={<LockIcon sx={{ fontSize: 12 }} />}>Read only</Badge>
                </>
              ) : horse.status === 'INACTIVE' ? (
                <Badge bg={DEC.pillBg} fg={DEC.pillFg}>Inactive</Badge>
              ) : (
                <Badge bg="#2E7D32" fg="#fff" icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}>Active</Badge>
              )}
            </Box>

            {/* Registered name directly under barn name */}
            {horse.registeredName && (
              <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: deceased ? DEC.label : 'text.secondary', mt: 0.15 }}>
                {horse.registeredName}
              </Typography>
            )}

            {/* Subtitle row: breed · gender · age · color */}
            {subtitleParts.length > 0 && (
              <Typography sx={{ fontSize: 13, color: deceased ? DEC.value : 'text.secondary', mt: 0.4 }}>
                {subtitleParts.join(' · ')}
              </Typography>
            )}

            {/* Stat chips */}
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mt: 1.25, alignItems: 'center' }}>
              {horse.weight != null && <StatChip deceased={deceased} label="Weight" value={`${horse.weight} kg`} />}
              {horse.height != null && <StatChip deceased={deceased} label="Height" value={`${horse.height} hh`} />}
              {horse.peiNumber && (
                <StatChip deceased={deceased} label="PEI number" value={horse.peiNumber} />
              )}
              {/* computeAgeYears non-null ⇒ DOB known ⇒ formatDob returns a real date. */}
              {age !== null && (
                /* testId: the timezone-matrix CONTROL case (e2e/specs/timezone-rendering.spec.ts).
                   formatDob parses the date PART, so this chip must read identically at UTC-11,
                   UTC and UTC+14 — a suite that goes red in every zone would otherwise be
                   indistinguishable from a broken harness. */
                <StatChip deceased={deceased} label="Date of birth" value={formatDob(horse.dateOfBirth)} testId="patient-dob" />
              )}
              {/* INSTANT — derived from `Appointment.date` (DATE-KINDS §1.3:
                  appointments.controller.ts converts the posted wall clock to
                  UTC with the sender's zone), so its only correct zone is the
                  clinic's. Same DISPLAY format as the DOB chip beside it,
                  different KIND rule — see DAY_FORMAT above. */}
              <StatChip deceased={deceased} label="Last visit" value={formatInstant(stats.lastVisitDate, DAY_FORMAT) || '—'} testId="patient-last-visit" />
              <StatChip deceased={deceased} label="Total records" value={stats.totalRecords ?? 0} />
              {showBilling && (
                <StatChip
                  deceased={deceased}
                  label="Balance"
                  value={formatDollars(balance)}
                  alert={hasBalance}
                  // A5c: every figure here renders through `money.ts`
                  // (.claude/rules/frontend.md) — a raw toFixed/toLocaleString
                  // prints 2712.5 as "$2,712.5" on a card clinics read for money.
                  // Wording matches the Billing tile caption's second half, not
                  // its first: this balance IS net, so "already deducted" is the
                  // honest clause and "+" would read as money still to come.
                  hint={
                    (stats.processingAmount ?? 0) > 0
                      ? `${formatDollars(stats.processingAmount!)} in flight (ACH), already deducted`
                      : undefined
                  }
                />
              )}
              {showBilling && <StatChip deceased={deceased} label="Open invoices" value={stats.openInvoiceCount ?? 0} alert={(stats.openInvoiceCount ?? 0) > 0 && hasBalance} />}
            </Box>

            {/* Alerts row — always visible when alerts exist; add/edit hidden for deceased */}
            {(alerts.length > 0 || (!deceased && onAddAlert)) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                {alerts.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: 'warning.dark' }}>
                      <WarningAmberIcon sx={{ fontSize: 15 }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Alerts:</Typography>
                    </Box>
                    {alerts.map((alert) => (
                      <Chip
                        key={alert.id}
                        label={alert.text}
                        size="small"
                        sx={{
                          bgcolor: alert.isCritical ? '#D32F2F' : '#E65100',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: 12,
                          height: 24,
                          borderRadius: '999px',
                          '& .MuiChip-label': { px: 1.25 },
                        }}
                      />
                    ))}
                    {!deceased && onAddAlert && (
                      <Tooltip title="Manage alerts" arrow>
                        <IconButton size="small" onClick={onAddAlert} sx={{ p: 0.4 }}>
                          <EditIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
                {alerts.length === 0 && !deceased && onAddAlert && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onAddAlert}
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: 'none',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      py: 0.5,
                      px: 1.25,
                      borderRadius: 1.5,
                      '&:hover': { borderColor: 'text.secondary', bgcolor: 'transparent' },
                    }}
                  >
                    + Add Alert
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Middle — location (barn + full address), in the open space left of
            Ownership. Falls back to the bare location string for horses whose
            barn has no address (or a legacy location with no Barn record). */}
        {(horse.barn || horse.location) && (
          <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: deceased ? DEC.label : 'text.secondary', mb: 1 }}>
              Location
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6 }}>
              <LocationOnIcon sx={{ fontSize: 16, mt: '1px', color: deceased ? DEC.label : 'text.secondary', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: deceased ? DEC.value : 'text.primary' }}>
                  {horse.barn?.name ?? horse.location}
                </Typography>
                {horse.barn?.street && (
                  <Typography sx={{ fontSize: 12, color: deceased ? DEC.label : 'text.secondary' }}>
                    {horse.barn.street}
                  </Typography>
                )}
                {(horse.barn?.city || horse.barn?.state || horse.barn?.zip) && (
                  <Typography sx={{ fontSize: 12, color: deceased ? DEC.label : 'text.secondary' }}>
                    {[horse.barn?.city, [horse.barn?.state, horse.barn?.zip].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Vertical divider */}
        <Box sx={{ borderLeft: '0.5px solid', borderColor: deceased ? DEC.cardBorder : 'divider', display: { xs: 'none', md: 'block' } }} />

        {/* Right — ownership */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: deceased ? DEC.label : 'text.secondary' }}>
              Ownership
            </Typography>
            <Typography sx={{ fontSize: 11, color: deceased ? DEC.label : 'text.secondary' }}>
              {Math.round(totalPct)}% assigned
            </Typography>
          </Box>

          <Stack spacing={1}>
            {owners.map((owner, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    bgcolor: deceased ? DEC.pillBg : OWNER_COLORS[i % OWNER_COLORS.length],
                    color: deceased ? DEC.value : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 600,
                  }}
                >
                  {initials(owner.name || '?')}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: deceased ? DEC.value : 'text.primary' }}>
                    {owner.name || 'Unnamed owner'}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: deceased ? DEC.label : 'text.secondary' }}>
                    {['Owner', owner.phone].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {owner.ownershipPercentage != null && (
                  <Badge bg={deceased ? DEC.pillBg : SUBTLE_BG} fg={deceased ? DEC.pillFg : 'text.secondary'}>
                    {owner.ownershipPercentage}%
                  </Badge>
                )}
              </Box>
            ))}

            {horse.insuranceCarrier && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.5 }}>
                <VerifiedUserIcon sx={{ fontSize: 14, color: deceased ? DEC.label : 'text.secondary' }} />
                <Typography sx={{ fontSize: 12, color: deceased ? DEC.value : 'text.secondary' }}>
                  {horse.insuranceCarrier}
                  {horse.insurancePolicyNumber ? ` · Policy #${horse.insurancePolicyNumber}` : ''}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {/* Optional action controls, top-right */}
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Box>

      {/* Horizontal rule */}
      <Box sx={{ borderTop: '0.5px solid', borderColor: deceased ? DEC.cardBorder : 'divider', my: 1.5 }} />

      {/* Bottom metadata row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.25 }}>
        <MetaCell deceased={deceased} label="Microchip" value={horse.microchipId || '—'} />
        <MetaCell deceased={deceased} label="Registration" value={horse.registrationNumber || '—'} />
        {deceased ? (
          /* CALENDAR — `Horse.dateOfDeath` (DATE-KINDS §1.1), the date part, never re-zoned. */
          <MetaCell deceased label="Date of death" value={formatCalendarDate(horse.dateOfDeath, DAY_FORMAT) || '—'} />
        ) : (
          <MetaCell
            deceased={false}
            label="Coggins / EIA"
            value={<>
              {/* THE NUMBER, at last. `cogginsAccession` has been declared here
                * since the card shipped and NO caller ever passed it, so every
                * chart in the app printed the literal 'EIA' — the fallback of a
                * dead prop, which reads like data and is not. Only a `LabResult`
                * carries an accession, which is why wiring it needed the lab arm
                * to exist first; a legacy vaccination-path Coggins still has
                * none and still degrades to 'EIA'. Monospace so it reads as an
                * identifier rather than a word. */}
              <Box
                component="span"
                data-testid="coggins-accession"
                sx={cogginsAccession ? { fontFamily: 'monospace', fontSize: 12 } : undefined}
              >
                {cogginsAccession || 'EIA'}
              </Box>
              <CogginsBadge expiry={cogginsExpiry} hasRecord={cogginsHasRecord} positive={cogginsPositive} />
              {/* The certificate itself, one click from the header. A negative
                * Coggins is a travel/sale document someone is usually being
                * asked to PRODUCE, so making them walk to the Labs tab for it is
                * the wrong shape.
                *
                * ⚠ NOT an `<a href>`, and the comment here used to claim the
                * opposite ("a plain link, so it inherits the browser's session").
                * It cannot, for two independent reasons. (1) WRONG ORIGIN: the API
                * is a different origin (`REACT_APP_API_URL`), `main.ts` sets no
                * global `api` prefix, `nginx.conf` has no `/api` location and there
                * is no CRA `proxy` — so `/api/...` resolves against the WEB origin
                * and returns index.html. (2) NO CREDENTIAL: `JwtAuthGuard` reads
                * `ExtractJwt.fromAuthHeaderAsBearerToken()`, and a browser-issued
                * anchor request carries no Authorization header. `downloadAuthedBlob`
                * is the house pattern for exactly this — its own docstring says
                * "use for download buttons where `<a href>` won't carry the bearer
                * token" — and it dates from the 2026-05 incident of the same shape. */}
              {cogginsCertificateAttachmentId && (
                <CogginsCertificateButton
                  attachmentId={cogginsCertificateAttachmentId}
                  horseName={horse.name}
                />
              )}
            </>}
          />
        )}
        <MetaCell deceased={deceased} label="Discipline" value={horse.discipline || '—'} />
        {deceased ? (
          <MetaCell deceased label="Cause of death" value={
            <Tooltip title={horse.causeOfDeath || ''}><Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{horse.causeOfDeath || '—'}</Box></Tooltip>
          } />
        ) : (
          <MetaCell
            deceased={false}
            label="Vaccinations"
            value={vaccinationSummary ? <VaccinationCounts summary={vaccinationSummary} onClick={onVaccinationsClick} /> : '—'}
          />
        )}
      </Box>
    </Box>
  );
};

export default PatientHeaderCard;
